import React, { PropsWithChildren, useState, useCallback, Children } from 'react';
import classNames from 'classnames';
import { NativeProps } from '@/src/components/interfaces';
import { CIcons } from '@/src/components/c-icon/CIcon';
import { CButton, CButtonColor, CButtonPattern, CButtonShape } from '@/src/components/c-button/CButton';
import { CFlex, AlignItems, FlexDirection } from '@/src/components/c-flex/CFlex';
import { CBox } from '@/src/components/c-box/CBox';
import { CText, CTextSize } from '@/src/components/c-text/CText';

export enum CFabPosition {
  BottomRight = 'bottom-right',
  BottomLeft = 'bottom-left',
  TopRight = 'top-right',
  TopLeft = 'top-left',
}

export enum CFabDirection {
  Up = 'up',
  Down = 'down',
}

export interface CFabItemProps extends NativeProps {
  icon: CIcons;
  label?: string;
  color?: CButtonColor;
  pattern?: CButtonPattern;
  shape?: CButtonShape;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export function CFabItem(_props: CFabItemProps): React.JSX.Element {
  return null as unknown as React.JSX.Element;
}

export interface CFabProps extends NativeProps {
  icon?: CIcons;
  direction?: CFabDirection;
  color?: CButtonColor;
  pattern?: CButtonPattern;
  shape?: CButtonShape;
  position?: CFabPosition;
  loading?: boolean;
  onClick?: () => void;
  ariaLabel: string;
}

export function CFab(props: PropsWithChildren<CFabProps>): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const icon = props.icon ?? CIcons.Plus;
  const direction = props.direction ?? CFabDirection.Up;
  const pattern = props.pattern ?? CButtonPattern.Primary;
  const shape = props.shape ?? CButtonShape.Rounded;

  const items = Children.toArray(props.children).filter(
    (child): child is React.ReactElement<CFabItemProps> =>
      React.isValidElement(child) && child.type === CFabItem
  );
  const hasItems = items.length > 0;

  const handleFabClick = useCallback(() => {
    if (!hasItems) {
      props.onClick?.();
    } else {
      setIsOpen(prev => !prev);
    }
  }, [hasItems, props.onClick]);

  const handleItemClick = useCallback((item: CFabItemProps) => {
    item.onClick?.();
    setIsOpen(false);
  }, []);

  return (
    <CFlex
      id={props.id}
      testSelector={props.testSelector}
      style={props.style}
      direction={direction === CFabDirection.Up ? FlexDirection.ColumnReverse : FlexDirection.Column}
      alignItems={AlignItems.Center}
      class={classNames('c-fab', { [`c-fab--position-${props.position}`]: props.position }, props.class)}
    >
      <CButton
        icon={icon}
        pattern={pattern}
        color={props.color}
        shape={shape}
        loading={props.loading}
        disabled={props.loading}
        onClick={handleFabClick}
        class={classNames('c-fab__button', { 'c-fab__button--open': hasItems && isOpen })}
      />

      {hasItems && (
        <CFlex
          direction={direction === CFabDirection.Up ? FlexDirection.ColumnReverse : FlexDirection.Column}
          alignItems={AlignItems.Center}
          class={classNames('c-fab__actions', { 'c-fab__actions--open': isOpen })}
        >
          {items.map((child, index) => {
            const item = child.props;
            const itemPattern = item.pattern ?? CButtonPattern.Secondary;
            const itemShape = item.shape ?? CButtonShape.Rounded;
            return (
              <CFlex
                key={item.id ?? index}
                alignItems={AlignItems.Center}
                class={classNames('c-fab__action', {
                  'c-fab__action--open': isOpen,
                  'c-fab__action--disabled': item.disabled,
                })}
                style={{ transitionDelay: isOpen ? `${index * 25}ms` : '0ms' }}
              >
                {item.label && (
                  <CBox class="c-fab__action-label">
                    <CText size={CTextSize.Sm}>{item.label}</CText>
                  </CBox>
                )}
                <CButton
                  icon={item.icon}
                  pattern={itemPattern}
                  color={item.color ?? props.color}
                  shape={itemShape}
                  disabled={item.disabled || item.loading}
                  loading={item.loading}
                  onClick={() => handleItemClick(item)}
                  class="c-fab__action-button"
                />
              </CFlex>
            );
          })}
        </CFlex>
      )}
    </CFlex>
  );
}
